import type { MirrorExternalTaskReceiptReference } from "@/lib/externalTaskExecutor";
import { cloudTaskId } from "../../supabase/functions/_shared/peopleTasksDomain";

interface UserResult {
  data: { user: { id: string } | null };
  error: unknown;
}
interface SessionResult {
  data: { session: { access_token: string; user: { id: string } } | null };
  error: unknown;
}

/** The injected transport also accepts the production Supabase client. */
export interface MirrorExternalTaskReceiptClient {
  auth: {
    getUser(): Promise<UserResult>;
    getSession(): Promise<SessionResult>;
  };
  functions: {
    invoke(name: string, options: {
      headers: Record<string, string>;
      body: Record<string, unknown>;
      signal?: AbortSignal;
      timeout?: number;
    }): Promise<{ data: unknown; error: unknown }>;
  };
}
export interface ConfirmMirrorExternalTaskReceiptOptions {
  client: MirrorExternalTaskReceiptClient;
  receipt: MirrorExternalTaskReceiptReference;
  language: string;
  isCurrent: () => boolean;
}

const uuid = (value: unknown): value is string => typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const nonempty = (value: unknown, max = 1500): value is string =>
  typeof value === "string" && value.trim().length > 0 && value.length <= max &&
  !Array.from(value).some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127);
const providerId = (value: unknown): value is string => { try { cloudTaskId(value); return true; } catch { return false; } };
const timeoutMs = 15_000;

/** Verify an owned server receipt and return its persisted acknowledgement.
 * Only the original locator is forwarded; no task data or provider write
 * is dispatched by this path. Obsolete and timed-out requests stay recoverable.
 */
export async function confirmMirrorExternalTaskReceipt({
  client, receipt, language, isCurrent,
}: ConfirmMirrorExternalTaskReceiptOptions): Promise<{ id: string; content: string } | null> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const current = () => !controller.signal.aborted && isCurrent();
  const confirm = async (): Promise<{ id: string; content: string } | null> => {
    if (!current() || !object(receipt) || !uuid(receipt.userId) || !uuid(receipt.sessionId) ||
      !uuid(receipt.action_id) || !nonempty(language, 50)) return null;
    const reference: MirrorExternalTaskReceiptReference = {
      provider: receipt.provider, action: receipt.action,
      idempotency_key: receipt.idempotency_key, action_id: receipt.action_id,
      userId: receipt.userId, sessionId: receipt.sessionId,
    };
    if (!["google_tasks", "microsoft_todo"].includes(reference.provider) ||
      !["create", "update", "complete", "reopen", "delete"].includes(reference.action) ||
      reference.idempotency_key !== "mirror_external_task_" + reference.action_id + "_" + reference.action + "_0") return null;

    const ownerSession = async () => {
      if (!current()) return null;
      const user = await client.auth.getUser();
      if (!current() || user.error || user.data.user?.id !== reference.userId) return null;
      const session = await client.auth.getSession();
      if (!current() || session.error || session.data.session?.user.id !== reference.userId ||
        !nonempty(session.data.session.access_token, 16_384)) return null;
      return session.data.session;
    };
    const before = await ownerSession();
    if (!current() || !before) return null;
    const token = before.access_token;
    const result = await client.functions.invoke("mirror-chat", {
      headers: { Authorization: "Bearer " + token },
      body: {
        promptType: "chat", sessionId: reference.sessionId,
        userMessage: "[External task receipt confirmation]", language,
        external_task_receipt: {
          provider: reference.provider, action: reference.action,
          idempotency_key: reference.idempotency_key, action_id: reference.action_id,
        },
      },
      signal: controller.signal, timeout: timeoutMs,
    });
    if (!current() || result.error) return null;
    const data = result.data;
    if (!object(data) || data.ok !== true || data.error != null ||
      typeof data.response !== "string" || !data.response.trim() || data.response.length > 12_000 ||
      data.session_id !== reference.sessionId || !uuid(data.message_id) ||
      (data.user_id !== undefined && data.user_id !== reference.userId) ||
      !Array.isArray(data.proposals) || data.proposals.length !== 0 ||
      !Array.isArray(data.action_results) || data.action_results.length !== 1) return null;
    const action = data.action_results[0];
    if (!object(action) || action.ok !== true || action.confirmed !== true ||
      action.status !== "succeeded" || action.scope !== "external" ||
      action.evidence !== "connector_receipt" || action.operation !== "verify_external_task_receipt" ||
      action.provider !== reference.provider || action.action !== reference.action ||
      action.capability !== "people:" + reference.action ||
      (action.unchanged !== undefined && typeof action.unchanged !== "boolean") ||
      (action.unchanged === true && reference.action === "create") ||
      action.action_id !== reference.action_id || !uuid(action.receipt_id) || !providerId(action.external_id) || !providerId(action.list_id) ||
      (action.user_id !== undefined && action.user_id !== reference.userId) ||
      (action.session_id !== undefined && action.session_id !== reference.sessionId) ||
      (action.idempotency_key !== undefined && action.idempotency_key !== reference.idempotency_key)) return null;

    const acknowledgement = { id: data.message_id, content: data.response };
    if (!await ownerSession() || !current()) return null;
    return acknowledgement;
  };
  try {
    const deadline = new Promise<null>(resolve => {
      timer = setTimeout(() => { controller.abort(); resolve(null); }, timeoutMs);
    });
    return await Promise.race([confirm(), deadline]);
  } catch {
    // Expected unavailable confirmations stay in the journal for recovery.
    // Do not log tokens, task contents, receipt payloads or provider errors.
    console.warn("[mirror] External task receipt confirmation unavailable");
    return null;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
